import {
  Button,
  CircleCheckIcon,
  CircleEmptyAlertIcon,
  ColumnDefinition,
  DataTable,
  DataTableProps,
  DownloadIcon,
  EntityInfo,
  IndicatorLabel,
  IndicatorLabelColor,
  LightBulbIcon,
  PendingIcon,
  Popover,
  RowInfo,
  Tooltip,
  TooltipContent,
  Typography,
} from "@codat/orchard-ui";
import { FileOutputType } from "api/public-service";
import {
  ReportStatus,
  SpendInsightsCompanies,
} from "api/schema/insights/spendInsightsCompaniesSchema";
import { useSpendInsightsProductSettings } from "contexts/SpendInsightsProductSettingsContext";
import { useGetCompany } from "hooks/query/useGetCompany";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { formatDateAndTimeWithShortString } from "utils/formatDate";
import formatUrl from "utils/formatUrl";

import { paths } from "components/App/paths";
import Pagination from "components/Pagination";
import LinkModal from "pages/Companies/LinkModal";

import styles from "./CompaniesTable.module.scss";
import { DownloadMenu } from "./DownloadMenu";

type CompaniesTableProps = Pick<
  DataTableProps<RowData[], ColumnKeys>,
  "isSkeleton" | "skeletonRows"
> & {
  data: SpendInsightsCompanies | undefined;
  onSort?: (sort: string) => void;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
};

export type RowData =
  | {
      key: "name";
      value: {
        name: string;
        id: string;
      };
    }
  | {
      key: "status";
      value: {
        status: ReportStatus;
      };
    }
  | {
      key: "lastUpdatedUtc";
      value: {
        lastUpdatedUtc: string | undefined;
      };
    }
  | {
      key: "actions";
      value: {
        onInsightsClick: () => void;
      };
    };

export type ColumnKeys = "name" | "status" | "lastUpdatedUtc" | "actions";

interface CompanyActionsCellProps {
  value: {
    handleDownloadMenuOpen: (companyId: string | null) => void;
    isDownloadMenuOpen: boolean;
    onInsightsClick: () => void;
    company: {
      name: string;
      id: string;
    };
    status: string;
    report: {
      outputTypes: FileOutputType[] | undefined;
      reportType: string | undefined;
      lastUpdatedUtc: string | undefined;
    };
  };
}

const CompanyActionsCell = ({ value }: CompanyActionsCellProps) => {
  const { data: spendInsightsSettings } = useSpendInsightsProductSettings();
  const downloadEnabled = value.status === "Complete";
  const genAIInsightsEnabled = spendInsightsSettings?.genAIInsights;

  return (
    <div className={styles.actionsWrapper}>
      <Popover
        isOpen={value.isDownloadMenuOpen}
        onClose={() => value.handleDownloadMenuOpen(null)}
        content={
          <DownloadMenu
            companyId={value.company.id}
            status={value.status}
            reportsOverview={value.report}
          />
        }
        placement="left-start"
      >
        <Button
          label={`Get insights on ${value.company.name}`}
          onClick={() => value.handleDownloadMenuOpen(value.company.id)}
          size="small"
          color="interface"
          variant={downloadEnabled ? "secondary" : "tertiary"}
          data-testid="download-report-btn"
          disabled={!downloadEnabled}
        >
          <DownloadIcon />
        </Button>
      </Popover>
      {genAIInsightsEnabled && (
        <Button
          label={`Get insights on ${value.company.name}`}
          onClick={value.onInsightsClick}
          size="small"
          data-testid="insights-btn"
        >
          <LightBulbIcon />
        </Button>
      )}
    </div>
  );
};

interface StatusCellProps {
  value: {
    status: ReportStatus;
    companyId: string;
    onRequestDataClick: () => void;
    isLoadingData: boolean;
    showTooltipCompanyId: string | null;
    setShowTooltipCompanyId: (companyId: string | null) => void;
  };
}

const getStatusLabelColour = (status: ReportStatus): IndicatorLabelColor => {
  switch (status) {
    case "InProgress":
      return "action";
    case "Complete":
      return "positive";
    case "Error":
      return "warning";
    default:
      return "interface";
  }
};
const getStatusLabelIcon = (status: ReportStatus): React.ReactNode => {
  switch (status) {
    case "InProgress":
      return <PendingIcon />;
    case "Complete":
      return <CircleCheckIcon />;
    case "Error":
      return <CircleEmptyAlertIcon />;
    default:
      return null;
  }
};
const getStatusLabelText = (status: ReportStatus): string => {
  switch (status) {
    case "InProgress":
      return "Generating";
    case "Complete":
      return "Available";
    case "Error":
      return "Unavailable";
    default:
      return status;
  }
};
const StatusCell = ({ value }: StatusCellProps) => {
  const indicatorLabel = (
    <IndicatorLabel
      leftIcon={getStatusLabelIcon(value.status)}
      color={getStatusLabelColour(value.status)}
      label={getStatusLabelText(value.status)}
    />
  );

  switch (value.status) {
    case "PendingAuth":
      return (
        <Button
          label="Request data"
          className={styles.requestDataButton}
          onClick={value.onRequestDataClick}
          size="small"
          data-testid="request-data-button"
          variant="secondary"
          isLoading={value.isLoadingData}
          spinnerAlign="center"
        />
      );
    case "Error":
      return (
        <Tooltip
          data-testid="error-status-tooltip"
          isOpen={value.showTooltipCompanyId === value.companyId}
          content={
            <TooltipContent>
              If you want to inquire further with support, quote the company ID:{" "}
              {value.companyId}
            </TooltipContent>
          }
        >
          <div
            data-testid="error-status-wrapper"
            onMouseOver={() => value.setShowTooltipCompanyId(value.companyId)}
            onMouseOut={() => value.setShowTooltipCompanyId(null)}
          >
            {indicatorLabel}
          </div>
        </Tooltip>
      );
    default:
      return indicatorLabel;
  }
};

const CompaniesTable = ({
  isSkeleton,
  data,
  page,
  setPage,
  pageSize,
  setPageSize,
}: CompaniesTableProps) => {
  const history = useHistory();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(
    null
  );
  const [requestDataCompanyId, setRequestDataCompanyId] = useState<string>();
  const [showTooltipCompanyId, setShowTooltipCompanyId] = useState<
    string | null
  >(null);
  const { data: fetchedCompany, isLoading: fetchingCompany } = useGetCompany({
    companyId: requestDataCompanyId,
  });

  const tableRows: RowInfo<RowData[]>[] =
    data?.results.map((result) => ({
      data: [
        {
          key: "name",
          value: {
            name: result.name,
            id: result.id,
          },
        },
        {
          key: "status",
          value: {
            status: result.status,
            companyId: result.id,
            onRequestDataClick: () => setRequestDataCompanyId(result.id),
            isLoadingData:
              requestDataCompanyId === result.id && fetchingCompany,
            showTooltipCompanyId,
            setShowTooltipCompanyId,
          },
        },
        {
          key: "lastUpdatedUtc",
          value: {
            lastUpdatedUtc: result.reportsOverview?.lastUpdatedUtc,
          },
        },
        {
          key: "actions",
          value: {
            company: { name: result.name, id: result.id },
            status: result.status,
            report: result.reportsOverview,
            handleDownloadMenuOpen: (id: string) =>
              setSelectedCompanyId(id === selectedCompanyId ? null : id),
            isDownloadMenuOpen: result.id === selectedCompanyId,
            onInsightsClick: () =>
              history.push(
                formatUrl(paths.spendInsights.company.INSIGHTS, {
                  companyId: result.id,
                })
              ),
          },
        },
      ],
    })) ?? [];

  const tableColumns: ColumnDefinition<ColumnKeys>[] =
    [
      {
        key: "name",
        label: "Company name",
        component: ({ value }) => (
          <EntityInfo
            title={value.name}
            subtitle={value.id}
            data-testid="companies-table-name"
          />
        ),
      },
      {
        key: "status",
        label: "Latest report status",
        component: StatusCell,
      },
      {
        key: "lastUpdatedUtc",
        label: "Last updated",
        component: ({ value }) => (
          <Typography as="div" className={styles.lastUpdated} noMargin>
            {formatDateAndTimeWithShortString(value.lastUpdatedUtc, true)}
          </Typography>
        ),
      },
      {
        key: "actions",
        label: "",
        align: "right",
        component: CompanyActionsCell,
      },
    ] ?? [];

  return (
    <>
      <DataTable
        className={styles.table}
        isLayoutAuto
        hasBorder
        isSkeleton={isSkeleton}
        skeletonRows={pageSize}
        rowsInfo={tableRows}
        columnDefs={tableColumns}
        data-testid="companies-table"
      />
      <Pagination
        label="Pagination"
        pageIndex={page}
        perPage={pageSize}
        perPageOptions={[10, 20, 50]}
        total={data?.totalResults || 0}
        onUpdatePageIndex={setPage}
        onUpdatePerPage={setPageSize}
        isSkeleton={isSkeleton}
      />
      {fetchedCompany && (
        <LinkModal
          company={fetchedCompany}
          onClose={() => setRequestDataCompanyId(undefined)}
        />
      )}
    </>
  );
};

export default CompaniesTable;
